/**
 * Positional bottom-edge measurement: elements marked `data-bottom-edge`
 * declare "I occupy the bottom edge; floating UI must clear me", and the
 * probe measures where they are actually painted. `getBoundingClientRect()`
 * is post-transform and post-sticky-pin, and occlusion is derived from a hit
 * test — a covering sheet wins it without being tagged — so a declaration
 * can never disagree with where the bar really is.
 *
 * Base UI's modal popups mount an invisible full-viewport click-catcher that
 * would otherwise win the hit test over every bar in the layout — it is
 * skipped in the hit stack (see MODAL_CLICK_CATCHER_SELECTOR).
 *
 * While Next's dev error overlay is open (`<nextjs-portal>`, dev builds only)
 * it wins every hit test and the inset drops to zero. Accepted — nothing
 * floating matters under a full-screen error screen — but headless dev-build
 * runs must check for an open overlay.
 */

import { getViewportRect, type ViewportRect } from "@/lib/bottom-edge/viewport"

export const BOTTOM_EDGE_ATTR = "data-bottom-edge"

/**
 * The attribute value says which side of the measurement an element is on:
 * bare `""` is bottom furniture (enumerated and measured); `"avoid"` is
 * floating UI that sits OVER the band rather than forming it, which the probe
 * sees through. These are positioned by *reading* `--bottom-inset` (toast
 * viewport, audio guide player, voice mini-player): at the unset-fallback slot
 * a reader can sit exactly on the bar it is waiting to clear, and counting
 * that as cover deadlocks the loop shut. They get the mark from
 * `bottomInsetRef` (writer.ts) when they register. Anything beneath them in
 * the hit stack still counts.
 *
 * `"avoid"` is for floating UI over the band, not a general opt-out: a sheet
 * over the bottom edge must count as cover. Declare it with this constant — a
 * typo'd value silently demotes a floater to furniture.
 */
export const BOTTOM_EDGE_AVOID = "avoid"
const AVOID_SELECTOR = `[${BOTTOM_EDGE_ATTR}="${BOTTOM_EDGE_AVOID}"]`

/**
 * Base UI's modal popups (select, menu) mount an invisible full-viewport
 * click-catcher carrying this attribute. It paints nothing, but it hit-tests
 * — and being portaled to <body> with no z-index it beats every bar inside
 * the layout's `isolate` wrapper on DOM order, since their z-indexes never
 * reach the root stacking context. Left in the stack, opening any modal menu
 * read all layout bars as covered and dropped the inset for the duration.
 * Skipped with matches(), not closest(): Base UI stamps the same attribute
 * on the app root to mark it inert while a modal is open, and a closest()
 * walk would skip the bars' own entries too.
 */
const MODAL_CLICK_CATCHER_SELECTOR = "[data-base-ui-inert]"
/** The bars: every marked element except the `"avoid"` readers. */
export const BOTTOM_EDGE_SELECTOR = `[${BOTTOM_EDGE_ATTR}]:not(${AVOID_SELECTOR})`

export type BarStatus =
  /** Painted at the edge — counts. */
  | "visible"
  /** Something is painted over it — does not count. */
  | "covered"
  /**
   * Not painted at all: `visibility: hidden`, `opacity: 0`, or entirely
   * off-screen.
   */
  | "hidden"
  /**
   * `pointer-events: none`, so hit-testing cannot see it either way. Counts
   * anyway — see `countsTowardReach`.
   */
  | "unverifiable"

/** A point hit-tested to settle a bar's status. Kept for diagnostics. */
export type ProbePoint = { x: number; y: number; hit: boolean }

export type BarHit = {
  element: Element
  rect: DOMRect
  /** Viewport-relative top edge of the bar. */
  top: number
  /** Distance from the bar's top edge to the bottom of the visible viewport. */
  reach: number
  status: BarStatus
  points: ProbePoint[]
  /** Topmost element painted over the bar, when `status` is `"covered"`. */
  coveredBy: Element | null
}

export type DropReason =
  /** No box: `display: none`, detached, or an empty portal container. */
  | "no-box"
  /** Bottom sits above the docked band — unpinned sticky, scrolled away. */
  | "above-band"
  /** Top is at or past the visible bottom — behind a keyboard, offscreen. */
  | "below-viewport"

/**
 * A marker that was enumerated but filtered on geometry, and the gate that
 * hit. Zero bars with nonzero drops is a different diagnosis from "no
 * markers exist".
 */
export type DroppedMarker = {
  element: Element
  rect: DOMRect
  reason: DropReason
}

export type ProbeResult = {
  /** Pixels of the bottom edge that floating UI must clear. */
  reach: number
  /** Every marked element considered, including ones that did not count. */
  bars: BarHit[]
  /** Markers enumerated but filtered out on geometry, with the gate that hit. */
  dropped: DroppedMarker[]
  /** Hit-test count, for cost accounting. */
  hitTests: number
}

/**
 * How far above the bottom edge a bar's own bottom may sit and still count
 * as docked, as a fraction of the visible viewport height. Needed because a
 * `position: sticky` bar carries its marker while unpinned mid-document,
 * while several bars legitimately float a fixed gap above the edge. Tested
 * against the bar's *bottom*, so anything flush passes at any ratio.
 */
const BAND_RATIO = 0.25

/**
 * A bar we could not verify still counts: over-reserving leaves a gap,
 * under-reserving puts a toast on top of the composer. `pointer-events: none`
 * is the one case hit-testing cannot answer, so it resolves conservatively.
 */
function countsTowardReach(status: BarStatus): boolean {
  return status === "visible" || status === "unverifiable"
}

type Verdict = {
  status: BarStatus
  points: ProbePoint[]
  coveredBy: Element | null
}

/**
 * `checkVisibility` walks ancestors, catching an `opacity: 0` *wrapper* the
 * own-element read misses. jsdom lacks it, so tests exercise the fallback.
 */
function canPaint(el: Element, style: CSSStyleDeclaration): boolean {
  if (typeof el.checkVisibility === "function") {
    return el.checkVisibility({ opacityProperty: true })
  }
  return style.opacity !== "0"
}

/**
 * Is this bar actually painted where its rect says it is? The centre sample
 * settles the common case in one hit test; the flanking points matter when a
 * half-width sheet covers one side of the nav and the uncovered side is
 * still furniture to clear.
 */
function verify(el: Element, rect: DOMRect, viewport: ViewportRect): Verdict {
  const left = Math.max(viewport.left, rect.left)
  const right = Math.min(viewport.right, rect.right) - 1
  const top = Math.max(viewport.top, rect.top)
  const bottom = Math.min(viewport.bottom, rect.bottom) - 1
  if (right < left || bottom < top) {
    return { status: "hidden", points: [], coveredBy: null }
  }

  // Opacity is settled before sampling and visibility after, because the two
  // invisibilities fail sampling in opposite directions: `opacity: 0` still
  // wins hit tests (a transparent bar would read as painted) and hides its
  // whole subtree, while `visibility: hidden` is skipped by hit tests but a
  // descendant can override it back — so any hit inside a style-hidden bar
  // proves something is painted there. (`display: none` has no box; the
  // caller already dropped it.)
  const style = getComputedStyle(el)
  if (!canPaint(el, style)) {
    return { status: "hidden", points: [], coveredBy: null }
  }

  const y = Math.round((top + bottom) / 2)
  const width = right - left
  const points: ProbePoint[] = []
  let coveredBy: Element | null = null

  for (const ratio of [0.5, 0.15, 0.85]) {
    const x = Math.round(left + width * ratio)
    const topMost =
      document
        .elementsFromPoint(x, y)
        .find(
          (entry) =>
            !entry.closest(AVOID_SELECTOR) &&
            !entry.matches(MODAL_CLICK_CATCHER_SELECTOR)
        ) ?? null
    const hit = !!topMost && (topMost === el || el.contains(topMost))
    points.push({ x, y, hit })
    if (hit) return { status: "visible", points, coveredBy: null }
    coveredBy ??= topMost
  }

  // Nothing sampled belonged to the bar. A style-hidden bar misses every
  // sample by design — "hidden", not "covered" — and per the ordering above
  // no visible descendant was painted either.
  if (style.visibility === "hidden") {
    return { status: "hidden", points, coveredBy: null }
  }

  // The computed value already accounts for a pe-none ancestor (inherited
  // property), and correctly skips the positioner pattern — a pe-none wrapper
  // whose pe-auto child is hittable through it.
  if (style.pointerEvents === "none") {
    return { status: "unverifiable", points, coveredBy }
  }
  return { status: "covered", points, coveredBy }
}

/**
 * `markers` is the bar set to measure. The writer passes the trigger
 * harness's live set so a probing frame never walks the document; standalone
 * callers enumerate. With no bars there is nothing to measure, so the
 * viewport read — the frame's first forced layout — is skipped too.
 */
export function probeBottomEdge(
  markers: Iterable<Element> = document.querySelectorAll(BOTTOM_EDGE_SELECTOR)
): ProbeResult {
  const result: ProbeResult = { reach: 0, bars: [], dropped: [], hitTests: 0 }
  const elements = Array.from(markers)
  if (elements.length === 0) return result

  const viewport = getViewportRect()
  if (viewport.height <= 0) return result

  const bandTop = viewport.bottom - viewport.height * BAND_RATIO

  for (const element of elements) {
    const rect = element.getBoundingClientRect()

    // `display: none`, detached, or a portal container sitting empty.
    if (rect.width <= 0 || rect.height <= 0) {
      result.dropped.push({ element, rect, reason: "no-box" })
      continue
    }

    // Docked at the bottom rather than merely on the page — drops an
    // unpinned sticky bar halfway down a long document.
    if (rect.bottom < bandTop) {
      result.dropped.push({ element, rect, reason: "above-band" })
      continue
    }
    if (rect.top >= viewport.bottom) {
      result.dropped.push({ element, rect, reason: "below-viewport" })
      continue
    }

    const { status, points, coveredBy } = verify(element, rect, viewport)
    result.hitTests += points.length

    const top = Math.round(rect.top)
    const bar: BarHit = {
      element,
      rect,
      top,
      // Clamped so a bar taller than the screen can't push readers off it.
      reach: Math.min(viewport.height, Math.max(0, viewport.bottom - top)),
      status,
      points,
      coveredBy,
    }
    result.bars.push(bar)

    if (countsTowardReach(status) && bar.reach > result.reach) {
      result.reach = bar.reach
    }
  }

  return result
}
